import * as React from 'react';
import {
  Box,
  Avatar,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemContent,
  Modal,
  ModalDialog,
  Option,
  Select,
  Sheet,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import {
  AssignmentIndRounded,
  AssignmentRounded,
  CheckCircle,
  Comment,
  DoneAllRounded,
  Email,
  ErrorOutlineRounded,
  FavoriteBorder,
  Info,
  LocationOn,
  LocationOnRounded,
  Person,
  Phone,
  RefreshRounded,
  SearchRounded,
  SendRounded,
  SwapHorizRounded,
  Timeline,
  WhatsApp,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/joy/styles';

import {
  ApiError,
  assignCongregationApplication,
  fetchCongregationAdminApplications,
  fetchCongregationApplication,
  fetchCongregationMasters,
  overrideCongregationApplication,
  type CongregationAdminFilter,
} from '../../api';
import { useCongregationAccess } from '../../congregation/context/CongregationAccessContext';
import { normalizeMastersPayload } from '../../congregation/utils';
import { useSnackbarStore } from '../../store/useSnackbarStore';
import type {
  CongregationAdminAssignInput,
  CongregationAdminOverrideInput,
  CongregationApplication,
  CongregationApplicationDetail,
  CongregationApplicationEvent,
  CongregationApplicationStatus,
  CongregationMastersPayload,
  CongregationSpiritualGuide,
} from '../../types/congregation';

const statusOptions = [
  { value: '' as const, label: 'All statuses' },
  { value: 'SUBMITTED' as const, label: 'Submitted' },
  { value: 'ASSIGNED_TO_MENTOR' as const, label: 'Assigned to caretaker' },
  { value: 'PENDING' as const, label: 'Pending (submitted + assigned)' },
  { value: 'ACCEPTED' as const, label: 'Accepted' },
  { value: 'REJECTED' as const, label: 'Rejected' },
  { value: 'WITHDRAWN' as const, label: 'Withdrawn' },
] as const satisfies ReadonlyArray<{
  value: '' | CongregationApplicationStatus | 'PENDING';
  label: string;
}>;

type StatusValue = (typeof statusOptions)[number]['value'];

type ActionKind = 'assign' | 'override';

type OverrideTarget = 'ACCEPTED' | 'REJECTED';

interface ActionStateBase {
  comment: string;
}

interface AssignState extends ActionStateBase {
  mentorGuideId: number | null;
}

interface OverrideState extends ActionStateBase {
  targetStatus: OverrideTarget;
}

const defaultAssignState: AssignState = {
  mentorGuideId: null,
  comment: '',
};

const defaultOverrideState: OverrideState = {
  targetStatus: 'ACCEPTED',
  comment: '',
};

const ASSIGN_COMMENT_MAX_LENGTH = 500;
const OVERRIDE_COMMENT_MIN_LENGTH = 10;
const OVERRIDE_COMMENT_MAX_LENGTH = 500;

function buildPendingTimeline(application: CongregationApplication): CongregationApplicationEvent[] {
  const events: CongregationApplicationEvent[] = [];
  if (application.submittedAt) {
    events.push({
      id: application.id * 10 + 1,
      type: 'SUBMIT',
      actorRole: 'DEVOTEE',
      actorName: application.applicantDisplayName ?? null,
      createdAt: application.submittedAt,
      comment: null,
    });
  }
  if (application.assignedMentorId && application.updatedAt) {
    events.push({
      id: application.id * 10 + 2,
      type: 'ASSIGN',
      actorRole: 'ADMIN',
      actorName: application.assignedMentorName ?? null,
      createdAt: application.updatedAt,
      comment: null,
    });
  }
  return events;
}

function getMentorName(mentorId: number | null | undefined, guides: CongregationSpiritualGuide[]) {
  if (!mentorId) return undefined;
  return guides.find((guide) => guide.memberId === mentorId || guide.id === mentorId)?.memberDisplayName;
}

export default function CongregationApplicationsPage() {
  const { membership } = useCongregationAccess();
  const { showSnackbar } = useSnackbarStore();
  const theme = useTheme();

  // Facilitators cannot access applications page
  const isAdmin = React.useMemo(
    () => membership?.roles?.some((role) => role.role === 'ADMIN' && !role.revokedAt) ?? false,
    [membership],
  );

  React.useEffect(() => {
    if (!isAdmin && membership) {
      showSnackbar('Facilitators do not have access to the applications page.', 'warning');
      window.location.href = '/congregation/dashboard';
    }
  }, [isAdmin, membership, showSnackbar]);

  if (!isAdmin && membership) {
    return (
      <Box sx={{ py: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress size="md" />
          <Typography level="title-md">Redirecting...</Typography>
        </Stack>
      </Box>
    );
  }

  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 8% 0%, rgba(255, 218, 170, 0.2) 0%, rgba(255, 218, 170, 0) 55%), radial-gradient(120% 140% at 95% 5%, rgba(165, 197, 255, 0.2) 0%, rgba(165, 197, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(248, 250, 255, 1) 45%, rgba(244, 246, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 15% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 15% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = React.useState(true);
  const [applications, setApplications] = React.useState<CongregationApplication[]>([]);
  const [masters, setMasters] = React.useState<CongregationMastersPayload | null>(null);
  const [detail, setDetail] = React.useState<CongregationApplicationDetail | null>(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState<number | null>(null);
  const [actionKind, setActionKind] = React.useState<ActionKind | null>(null);
  const [assignState, setAssignState] = React.useState<AssignState>(defaultAssignState);
  const [overrideState, setOverrideState] = React.useState<OverrideState>(defaultOverrideState);
  const [actionSubmitting, setActionSubmitting] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState(searchParams.get('q') ?? '');
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  const statusParam = searchParams.get('status');
  const statusFilter: StatusValue =
    statusParam && statusOptions.some((option) => option.value === statusParam)
      ? (statusParam as StatusValue)
      : '';
  const mentorFilter = searchParams.get('mentorId') ?? '';

  const updateParam = React.useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value && value.length > 0) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const loadMasters = React.useCallback(async () => {
    try {
      const payload = await fetchCongregationMasters();
      setMasters(normalizeMastersPayload(payload));
    } catch (error) {
      console.error('Failed to load congregation masters', error);
      showSnackbar('Unable to load congregation caretakers.', 'danger');
    }
  }, [showSnackbar]);

  const loadApplications = React.useCallback(async () => {
    const shouldShowSpinner = applications.length === 0;
    if (shouldShowSpinner) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }
    try {
      const params: CongregationAdminFilter = {};
      if (statusFilter && statusFilter !== 'PENDING') {
        params.status = statusFilter;
      }
      const response = await fetchCongregationAdminApplications(params);
      const fetchedApplications = response.applications ?? [];
      const filteredByStatus =
        statusFilter === 'PENDING'
          ? fetchedApplications.filter((application) =>
            application.status === 'SUBMITTED' ||
            application.status === 'ASSIGNED_TO_MENTOR',
          )
          : fetchedApplications;
      const filteredByMentor = (() => {
        if (!mentorFilter) {
          return filteredByStatus;
        }
        const parsedMentorId = Number(mentorFilter);
        if (Number.isNaN(parsedMentorId)) {
          return filteredByStatus;
        }
        return filteredByStatus.filter(
          (application) => application.assignedMentorId === parsedMentorId,
        );
      })();
      setApplications(filteredByMentor);
    } catch (error) {
      console.error('Failed to fetch congregation applications', error);
      showSnackbar('Unable to load congregation applications. Please retry.', 'danger');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [applications.length, mentorFilter, showSnackbar, statusFilter]);

  const loadDetail = React.useCallback(
    async (id: number) => {
      setDetailLoading(true);
      try {
        const response = await fetchCongregationApplication(id);
        setDetail(response);
      } catch (error) {
        console.error('Failed to load congregation application detail', error);
        showSnackbar('Unable to load application detail.', 'warning');
      } finally {
        setDetailLoading(false);
      }
    },
    [showSnackbar],
  );

  React.useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  React.useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  React.useEffect(() => {
    setSearchTerm(searchParams.get('q') ?? '');
  }, [searchParams]);

  React.useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    }
  }, [loadDetail, selectedId]);

  const filtered = React.useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return applications.filter((application) => {
      if (!normalizedSearch) return true;
      const haystacks = [
        application.comment ?? '',
        application.assignedMentorName ?? '',
        String(application.applicantId),
      ];
      return haystacks.some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [applications, searchTerm]);

  const selectedApplication = React.useMemo(
    () => filtered.find((application) => application.id === selectedId) ?? null,
    [filtered, selectedId],
  );

  const applicantId = React.useMemo(() => {
    const app = selectedApplication;
    if (!app) return null;
    return app.applicantMemberId ?? app.applicantId ?? null;
  }, [selectedApplication]);

  const applicantName = React.useMemo(() => {
    if (detail?.member?.displayName) return detail.member.displayName;
    if (selectedApplication?.applicantDisplayName) return selectedApplication.applicantDisplayName;

    const formPayload = (detail?.formData ?? selectedApplication?.formData) || null;
    const personal = formPayload && typeof formPayload === 'object'
      ? (formPayload as Record<string, any>).personal
      : null;
    if (personal && typeof personal === 'object') {
      return (
        (personal.displayName as string) ||
        (personal.legalName as string) ||
        (personal.name as string) ||
        null
      );
    }
    return null;
  }, [detail?.formData, detail?.member?.displayName, selectedApplication?.applicantDisplayName, selectedApplication?.formData]);

  const applicantContact = React.useMemo(() => {
    // Prefer member details if available
    if (detail?.member) {
      return {
        phone: detail.member.phone ?? null,
        email: detail.member.email ?? null,
      };
    }

    const formPayload = (detail?.formData ?? selectedApplication?.formData) || null;
    const personal = formPayload && typeof formPayload === 'object'
      ? (formPayload as Record<string, any>).personal
      : null;
    const phone = personal && typeof personal === 'object' ? (personal.phone as string | undefined) ?? null : null;
    const email = personal && typeof personal === 'object' ? (personal.email as string | undefined) ?? null : null;
    return { phone, email };
  }, [detail?.formData, detail?.member, selectedApplication?.formData]);

  const personalData = React.useMemo(() => {
    const formPayload = (detail?.formData ?? selectedApplication?.formData) || null;
    const personal = formPayload && typeof formPayload === 'object'
      ? (formPayload as Record<string, any>).personal
      : null;
    if (personal && typeof personal === 'object') {
      const spiritualMasterId = (personal.spiritualMasterId as number | undefined) ?? null;
      const spiritualMasterName = spiritualMasterId
        ? masters?.spiritualMasters?.find((master) => master.id === spiritualMasterId)?.name ?? null
        : null;
      return {
        city: (personal.city as string | undefined) ?? null,
        state: (personal.state as string | undefined) ?? null,
        phone: (personal.phone as string | undefined) ?? null,
        email: (personal.email as string | undefined) ?? null,
        gender: (personal.gender as string | undefined) ?? null,
        occupation: (personal.occupation as string | undefined) ?? null,
        spiritualMasterId,
        spiritualMasterName,
      };
    }
    return {
      city: null,
      state: null,
      phone: null,
      email: null,
      gender: null,
      occupation: null,
      spiritualMasterId: null,
      spiritualMasterName: null,
    };
  }, [detail?.formData, masters?.spiritualMasters, selectedApplication?.formData]);

  const devotionalData = React.useMemo(() => {
    const formPayload = (detail?.formData ?? selectedApplication?.formData) || null;
    const devotional = formPayload && typeof formPayload === 'object'
      ? (formPayload as Record<string, any>).devotional
      : null;
    if (devotional && typeof devotional === 'object') {
      return {
        chantingRounds: (devotional.chantingRounds as number | undefined) ?? null,
      };
    }
    return { chantingRounds: null };
  }, [detail?.formData, selectedApplication?.formData]);

  const currentEvents: CongregationApplicationEvent[] = React.useMemo(() => {
    if (detail?.events && detail.events.length > 0) {
      return detail.events;
    }
    if (selectedApplication) {
      return buildPendingTimeline(selectedApplication);
    }
    return [];
  }, [detail?.events, selectedApplication]);

  const assignedMentorId = React.useMemo(
    () => selectedApplication?.assignedMentorId ?? null,
    [selectedApplication?.assignedMentorId],
  );

  const mentorName = React.useMemo(
    () => getMentorName(assignedMentorId, masters?.spiritualGuides ?? []),
    [assignedMentorId, masters?.spiritualGuides],
  );

  const openDrawer = React.useCallback((application: CongregationApplication) => {
    setSelectedId(application.id);
  }, []);

  const closeDrawer = React.useCallback(() => {
    setSelectedId(null);
    setDetail(null);
    setActionKind(null);
    setAssignState(defaultAssignState);
    setOverrideState(defaultOverrideState);
  }, []);

  const allMentors = React.useMemo(
    () =>
      [...(masters?.spiritualGuides ?? [])].sort((a, b) =>
        a.memberDisplayName.localeCompare(b.memberDisplayName),
      ),
    [masters?.spiritualGuides],
  );

  const activeMentors = React.useMemo(
    () => allMentors.filter((guide) => guide.isActive !== false),
    [allMentors],
  );
  const applicationSummary = React.useMemo(() => {
    const total = applications.length;
    const submitted = applications.filter((application) => application.status === 'SUBMITTED').length;
    const assigned = applications.filter((application) => application.status === 'ASSIGNED_TO_MENTOR').length;
    const accepted = applications.filter((application) => application.status === 'ACCEPTED').length;
    const rejected = applications.filter((application) => application.status === 'REJECTED').length;
    const withdrawn = applications.filter((application) => application.status === 'WITHDRAWN').length;
    const pending = submitted + assigned;
    return { total, submitted, assigned, accepted, rejected, withdrawn, pending };
  }, [applications]);
  const handleClearFilters = React.useCallback(() => {
    updateParam('status', null);
    updateParam('mentorId', null);
    updateParam('q', null);
    setSearchTerm('');
  }, [updateParam]);

  const handleAssignSubmit = React.useCallback(async () => {
    if (!selectedId || !assignState.mentorGuideId) {
      showSnackbar('Select a caretaker before assigning.', 'warning');
      return;
    }
    const selectedMentor = allMentors.find(
      (guide) => guide.id === assignState.mentorGuideId,
    );
    if (!selectedMentor) {
      showSnackbar('Select a caretaker from the current roster before assigning.', 'warning');
      return;
    }
    if (selectedMentor.isActive === false) {
      showSnackbar('Cannot assign to an inactive caretaker. Reactivate them first.', 'warning');
      return;
    }
    const assignedCount = applications.filter((application) => {
      const assignedId = application.assignedMentorId;
      return assignedId === selectedMentor.id || assignedId === selectedMentor.memberId;
    }).length;
    if (
      selectedMentor.capacity != null &&
      selectedMentor.capacity >= 0 &&
      assignedCount >= selectedMentor.capacity
    ) {
      showSnackbar('This caretaker is at capacity. Choose another caretaker or adjust capacity.', 'warning');
      return;
    }

    const trimmedComment = assignState.comment.trim();
    const payload: CongregationAdminAssignInput = {
      mentorGuideId: assignState.mentorGuideId,
      comment: trimmedComment ? trimmedComment : undefined,
    };
    try {
      setActionSubmitting(true);
      await assignCongregationApplication(selectedId, payload);
      showSnackbar('Application assigned to caretaker.', 'success');
      await loadApplications();
      await loadDetail(selectedId);
      setActionKind(null);
      setAssignState(defaultAssignState);
    } catch (error) {
      console.error('Failed to assign caretaker', error);
      if (error instanceof ApiError && error.status === 409) {
        showSnackbar('Another admin already updated this application. Reloaded latest data.', 'warning');
        await loadApplications();
        await loadDetail(selectedId);
      } else {
        showSnackbar('Unable to assign caretaker. Refresh and try again.', 'danger');
      }
    } finally {
      setActionSubmitting(false);
    }
  }, [
    allMentors,
    applications,
    assignState.comment,
    assignState.mentorGuideId,
    loadApplications,
    loadDetail,
    selectedId,
    showSnackbar,
  ]);

  const handleOverrideSubmit = React.useCallback(async () => {
    if (!selectedId) return;
    const trimmedComment = overrideState.comment.trim();
    if (!trimmedComment) {
      showSnackbar('Provide a justification before overriding.', 'warning');
      return;
    }
    if (trimmedComment.length < OVERRIDE_COMMENT_MIN_LENGTH) {
      showSnackbar(
        `Override justification must be at least ${OVERRIDE_COMMENT_MIN_LENGTH} characters.`,
        'warning',
      );
      return;
    }
    const payload: CongregationAdminOverrideInput = {
      targetStatus: overrideState.targetStatus,
      comment: trimmedComment,
    };
    try {
      setActionSubmitting(true);
      await overrideCongregationApplication(selectedId, payload);
      showSnackbar('Override recorded.', 'success');
      await loadApplications();
      await loadDetail(selectedId);
      setActionKind(null);
      setOverrideState(defaultOverrideState);
    } catch (error) {
      console.error('Failed to override application', error);
      if (error instanceof ApiError && error.status === 409) {
        showSnackbar('This application already moved to a new status. Loaded the latest timeline.', 'warning');
        await loadApplications();
        await loadDetail(selectedId);
      } else {
        showSnackbar('Unable to override application. Refresh and retry.', 'danger');
      }
    } finally {
      setActionSubmitting(false);
    }
  }, [loadApplications, loadDetail, overrideState.comment, overrideState.targetStatus, selectedId, showSnackbar]);

  const assignableMentors = React.useMemo(() => {
    if (!selectedApplication?.assignedMentorId) {
      return activeMentors;
    }
    const existing = allMentors.find(
      (guide) =>
        guide.memberId === selectedApplication.assignedMentorId ||
        guide.id === selectedApplication.assignedMentorId,
    );
    if (
      existing &&
      existing.isActive === false &&
      !activeMentors.some((guide) => guide.memberId === existing.memberId)
    ) {
      return [...activeMentors, existing];
    }
    return activeMentors;
  }, [activeMentors, allMentors, selectedApplication?.assignedMentorId]);

  return (
    <Box>
      <Stack spacing={{ xs: 2.5, md: 3.5 }}>
        <Card
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 'xl',
            borderColor: 'divider',
            boxShadow: 'lg',
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              right: { xs: -60, md: -20 },
              top: { xs: 30, md: 10 },
              background: orbColor,
              filter: 'blur(6px)',
              animation: 'applications-float 8s ease-in-out infinite',
              '@keyframes applications-float': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ position: 'relative' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
                <Stack spacing={0.75}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <AssignmentRounded sx={{ fontSize: 28, color: 'primary.500' }} />
                    <Typography level="h2" sx={{ fontWeight: 800, color: heroTitle }}>
                      Applications
                    </Typography>
                  </Stack>
                  <Typography level="body-sm" sx={{ color: heroBody, maxWidth: 880 }}>
                    Route congregation applications to mentors, review devotional commitments, and manage administrative overrides.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip variant="soft" color="primary">
                    {applicationSummary.total} total
                  </Chip>
                  <Chip variant="soft" color="neutral">
                    {applicationSummary.pending} pending
                  </Chip>
                  <Chip variant="soft" color="success">
                    {applicationSummary.accepted} accepted
                  </Chip>
                  <Chip variant="soft" color="danger">
                    {applicationSummary.rejected} rejected
                  </Chip>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Intake flow
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {applicationSummary.submitted} submitted • {applicationSummary.assigned} assigned
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {applicationSummary.withdrawn} withdrawn across this filter.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Quick actions
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                      <Button
                        variant="soft"
                        startDecorator={<RefreshRounded />}
                        onClick={loadApplications}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Refresh list
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                      >
                        Clear filters
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Main Grid Layout */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: { xs: 2, md: 3 } }}>
          {/* Left panel - Applications list */}
          <Card variant="outlined" sx={{ borderRadius: 'xl', height: 'fit-content' }}>
            <CardContent sx={{ p: 2 }}>
              <Stack spacing={2}>
                {/* Filters */}
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Input
                      placeholder="Search applications..."
                      startDecorator={<SearchRounded />}
                      value={searchTerm}
                      onChange={(event) => {
                        const value = event.target.value;
                        setSearchTerm(value);
                        updateParam('q', value.trim().length > 0 ? value : null);
                      }}
                      fullWidth
                      size="sm"
                      sx={{ borderRadius: '999px', bgcolor: 'background.level1' }}
                    />
                    <IconButton variant="soft" color="neutral" size="sm" onClick={loadApplications}>
                      <RefreshRounded />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <FormControl sx={{ flex: 1 }}>
                      <Select
                        size="sm"
                        value={statusFilter}
                        onChange={(_, value) => updateParam('status', value ?? '')}
                        slotProps={{ listbox: { sx: { maxHeight: 280 } } }}
                        placeholder="All statuses"
                        sx={{ borderRadius: '999px' }}
                      >
                        {statusOptions.map((option) => (
                          <Option key={option.value} value={option.value}>
                            {option.label}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }}>
                      <Select
                        size="sm"
                        value={mentorFilter}
                        onChange={(_, value) => updateParam('mentorId', value ?? '')}
                        placeholder="Any caretaker"
                        slotProps={{ listbox: { sx: { maxHeight: 280 } } }}
                        sx={{ borderRadius: '999px' }}
                      >
                        <Option value="">Any caretaker</Option>
                        {allMentors.map((guide) => (
                          <Option key={guide.memberId} value={String(guide.memberId)}>
                            {guide.memberDisplayName}
                          </Option>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                </Stack>

                <Divider />

                {/* Applications list */}
                {loading ? (
                  <Stack spacing={1}>
                    {[1, 2, 3].map((key) => (
                      <Box key={key} sx={{ p: 2, borderRadius: 'sm', bgcolor: 'background.level1' }}>
                        <Stack spacing={1}>
                          <Box sx={{ width: '60%', height: 14, bgcolor: 'neutral.softBg', borderRadius: 'xs' }} />
                          <Box sx={{ width: '40%', height: 12, bgcolor: 'neutral.softBg', borderRadius: 'xs' }} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                ) : filtered.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography level="body-sm" color="neutral">
                      No applications found
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ '--ListItem-paddingY': '0.5rem', maxHeight: 'calc(100vh - 380px)', overflow: 'auto', pr: 0.5 }}>
                    {filtered.map((application) => {
                      const isSelected = selectedId === application.id;
                      const appFormData = (application as any).formData || {};
                      const appPersonal = appFormData.personal || {};
                      const appDevotional = appFormData.devotional || {};
                      const displayName = application.applicantDisplayName || appPersonal.displayName || `Applicant #${application.id}`;
                      const statusColorMap = {
                        ACCEPTED: 'success',
                        REJECTED: 'danger',
                        WITHDRAWN: 'neutral',
                      } as const;
                      const chipColor = statusColorMap[application.status as keyof typeof statusColorMap] || 'primary';

                      return (
                        <ListItem
                          key={application.id}
                          onClick={() => openDrawer(application)}
                          sx={{
                            borderRadius: 'md',
                            cursor: 'pointer',
                            bgcolor: isSelected ? 'primary.softBg' : 'transparent',
                            border: isSelected ? '1.5px solid' : '1px solid',
                            borderColor: isSelected ? 'primary.outlinedBorder' : 'divider',
                            transition: 'all 150ms ease',
                            p: 1.5,
                            '&:hover': {
                              bgcolor: isSelected ? 'primary.softBg' : 'neutral.softBg',
                              borderColor: 'primary.outlinedBorder',
                            },
                          }}
                        >
                          <ListItemContent>
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" alignItems="center" gap={1} sx={{ flex: 1 }}>
                                  <Avatar
                                    size="sm"
                                    sx={{
                                      bgcolor: chipColor + '.main',
                                      fontSize: 'sm',
                                      fontWeight: 700,
                                    }}
                                  >
                                    {(displayName?.charAt(0) || 'A').toUpperCase()}
                                  </Avatar>
                                  <Typography level="title-sm" fontWeight={700}>
                                    {displayName}
                                  </Typography>
                                </Stack>
                                <Chip size="sm" variant="solid" color={chipColor} sx={{ fontWeight: 600 }}>
                                  {application.status.replaceAll('_', ' ').toLowerCase()}
                                </Chip>
                              </Stack>
                              {(appPersonal.city || appPersonal.state) && (
                                <Stack direction="row" alignItems="center" gap={0.5} sx={{ ml: 4 }}>
                                  <LocationOnRounded sx={{ fontSize: 'sm', color: 'primary.main' }} />
                                  <Typography level="body-xs" color="neutral" sx={{ fontWeight: 500 }}>
                                    {appPersonal.city}{appPersonal.state && `, ${appPersonal.state}`}
                                  </Typography>
                                </Stack>
                              )}
                              <Stack direction="row" alignItems="center" gap={2} sx={{ ml: 4, mt: 0.5, flexWrap: 'wrap' }}>
                                {appDevotional.chantingRounds && (
                                  <Chip
                                    size="sm"
                                    variant="soft"
                                    color="success"
                                    sx={{ fontWeight: 600, fontSize: 'xs' }}
                                  >
                                    {appDevotional.chantingRounds} 🙏
                                  </Chip>
                                )}
                                <Typography level="body-xs" color="neutral" sx={{ fontWeight: 500 }}>
                                  {application.submittedAt ? dayjs(application.submittedAt).format('DD MMM YYYY') : 'date pending'}
                                </Typography>
                              </Stack>
                            </Stack>
                          </ListItemContent>
                        </ListItem>
                      );
                    })}
                  </List>
                )}

                {isRefreshing && !loading ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size="sm" />
                    <Typography level="body-xs" color="neutral">
                      Refreshing…
                    </Typography>
                  </Stack>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          {/* Right panel - Application details */}
          <Stack spacing={2.5} sx={{ minWidth: 0, maxHeight: 'calc(100vh - 240px)', overflow: 'auto', pr: 0.5 }}>
            {loading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
                <CircularProgress size="md" variant="soft" />
              </Box>
            ) : !selectedApplication ? (
              <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
                <Typography level="body-md" color="neutral">
                  Select an application to review
                </Typography>
              </Box>
            ) : (
              <>
                {/* Header with status */}
                <Card variant="outlined">
                  <CardContent sx={{ pb: 2 }}>
                    <Stack spacing={1.5}>
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2}>
                        <Stack spacing={1} sx={{ flex: 1 }}>
                          <Typography level="body-xs" textTransform="uppercase" sx={{ fontWeight: 700, letterSpacing: 0.5, color: 'primary.main' }}>
                            Applicant Profile
                          </Typography>
                          <Stack direction="row" alignItems="center" gap={1}>
                            <Avatar
                              size="md"
                              sx={{
                                bgcolor: 'primary.main',
                                fontSize: 'xl',
                                fontWeight: 700,
                              }}
                            >
                              {(applicantName?.charAt(0) || 'A').toUpperCase()}
                            </Avatar>
                            <Typography level="h3" sx={{ fontWeight: 700 }}>
                              {applicantName}
                            </Typography>
                          </Stack>
                        </Stack>
                        <Chip
                          variant="solid"
                          color={
                            selectedApplication.status === 'ACCEPTED'
                              ? 'success'
                              : selectedApplication.status === 'REJECTED'
                                ? 'danger'
                                : selectedApplication.status === 'WITHDRAWN'
                                  ? 'neutral'
                                  : 'primary'
                          }
                          sx={{ fontWeight: 600, fontSize: 'sm' }}
                        >
                          {selectedApplication.status.replaceAll('_', ' ').toLowerCase()}
                        </Chip>
                      </Stack>
                      <Divider sx={{ my: 0.5 }} />
                      <Stack direction="row" spacing={1.5} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {selectedApplication.assignedMentorName && (
                          <Chip
                            variant="outlined"
                            startDecorator={<CheckCircle sx={{ fontSize: 'md' }} />}
                            size="sm"
                            sx={{ fontWeight: 500 }}
                          >
                            Assigned to {selectedApplication.assignedMentorName}
                          </Chip>
                        )}
                        <Typography level="body-xs" sx={{ color: 'neutral.500', fontWeight: 500, my: 'auto' }}>
                          {selectedApplication.submittedAt ? `Submitted ${dayjs(selectedApplication.submittedAt).format('DD MMM YYYY')}` : 'Submission date pending'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Contact Information */}
                {(applicantContact.phone || applicantContact.email) && (
                  <Card variant="outlined" sx={{ borderColor: 'divider' }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                          <Person sx={{ fontSize: 'md', color: 'primary.main' }} />
                          <Typography level="title-sm" fontWeight={700}>Contact Information</Typography>
                        </Stack>
                        <Divider sx={{ my: 0.5 }} />
                        <Stack spacing={1.5}>
                          {applicantContact.phone && (
                            <Stack direction="row" gap={2} alignItems="flex-start">
                              <Phone sx={{ fontSize: 'md', color: 'primary.main', mt: 0.5 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography level="body-xs" textTransform="uppercase" sx={{ fontWeight: 700, color: 'neutral.600', letterSpacing: 0.3, mb: 0.25 }}>Phone</Typography>
                                <Typography level="body-sm" sx={{ fontWeight: 500, color: 'neutral.700' }}>{applicantContact.phone}</Typography>
                              </Box>
                            </Stack>
                          )}
                          {applicantContact.email && (
                            <Stack direction="row" gap={2} alignItems="flex-start">
                              <Email sx={{ fontSize: 'md', color: 'primary.main', mt: 0.5 }} />
                              <Box sx={{ flex: 1 }}>
                                <Typography level="body-xs" textTransform="uppercase" sx={{ fontWeight: 700, color: 'neutral.600', letterSpacing: 0.3, mb: 0.25 }}>Email</Typography>
                                <Typography level="body-sm" sx={{ fontWeight: 500, color: 'neutral.700' }}>{applicantContact.email}</Typography>
                              </Box>
                            </Stack>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Profile Snapshot */}
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: 'divider',
                  }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                        <LocationOn sx={{ fontSize: 'md', color: 'primary.main' }} />
                        <Typography level="title-sm" fontWeight={700}>Profile Snapshot</Typography>
                      </Stack>
                      <Divider sx={{ my: 0.5 }} />
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                          gap: 2,
                        }}
                      >
                        <Box>
                          <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>City</Typography>
                          <Typography level="body-sm">{personalData.city || '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>State</Typography>
                          <Typography level="body-sm">{personalData.state || '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>Occupation</Typography>
                          <Typography level="body-sm">{personalData.occupation || '—'}</Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>Spiritual master</Typography>
                          <Typography level="body-sm">
                            {personalData.spiritualMasterName || (personalData.spiritualMasterId ? `Master #${personalData.spiritualMasterId}` : '—')}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography level="body-xs" textTransform="uppercase" sx={{ fontWeight: 700, color: 'success.main', letterSpacing: 0.3, mb: 0.5 }}>Chanting Rounds</Typography>
                          <Typography level="h4" sx={{ color: 'success.main', fontWeight: 800 }}>{devotionalData.chantingRounds || '—'}</Typography>
                        </Box>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Application Details */}
                {(detail?.formData || selectedApplication?.formData) && (
                  <Card variant="outlined" sx={{ borderColor: 'divider' }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                          <AssignmentRounded sx={{ fontSize: 'md', color: 'primary.main' }} />
                          <Typography level="title-sm" fontWeight={700}>Application Details</Typography>
                        </Stack>
                        <Divider sx={{ my: 0.5 }} />
                        <Stack spacing={3}>
                          {(() => {
                            const payload = (detail?.formData ?? selectedApplication?.formData) || null;
                            if (!payload || typeof payload !== 'object') return null;

                            const personal = (payload as any).personal || {};
                            const family = (payload as any).family || {};
                            const devotional = (payload as any).devotional || {};

                            const hasPersonalContent = Object.values(personal).some(v => v !== null && v !== undefined && v !== '' && v !== false);
                            const hasFamilyContent = Object.values(family).some(v => v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0));
                            const hasDevotionalContent = Object.values(devotional).some(v => v !== null && v !== undefined && v !== '' && v !== 0 && (!Array.isArray(v) || v.length > 0));

                            return (
                              <>
                                {/* Personal Section */}
                                {hasPersonalContent && (
                                  <Box>
                                    <Typography level="body-xs" textTransform="uppercase" letterSpacing={0.7} sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary' }}>
                                      📋 Personal Information
                                    </Typography>
                                    <Stack spacing={1.2} sx={{ ml: 0.5 }}>
                                      {[
                                        { key: 'displayName', label: 'Display name', value: (personal as any).displayName },
                                        { key: 'legalName', label: 'Legal name', value: (personal as any).legalName },
                                        { key: 'phone', label: 'Phone', value: (personal as any).phone },
                                        { key: 'email', label: 'Email', value: (personal as any).email },
                                        { key: 'city', label: 'City', value: (personal as any).city },
                                        { key: 'state', label: 'State', value: (personal as any).state },
                                        { key: 'gender', label: 'Gender', value: (personal as any).gender },
                                        { key: 'maritalStatus', label: 'Marital status', value: (personal as any).maritalStatus },
                                        { key: 'occupation', label: 'Occupation', value: (personal as any).occupation },
                                        { key: 'initiatedWith', label: 'Initiated', value: (personal as any).initiatedWith },
                                        {
                                          key: 'spiritualMaster',
                                          label: 'Spiritual master',
                                          value:
                                            personalData.spiritualMasterName ||
                                            (personal as any).spiritualMasterId ||
                                            personalData.spiritualMasterId,
                                        },
                                      ]
                                        .filter((item) => item.value)
                                        .map((item) => (
                                          <Box key={item.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pb: 0.5 }}>
                                            <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                              {item.label}
                                            </Typography>
                                            <Typography level="body-sm" sx={{ fontWeight: 500, color: 'neutral.700', textAlign: 'right' }}>{String(item.value)}</Typography>
                                          </Box>
                                        ))}
                                    </Stack>
                                  </Box>
                                )}

                                {/* Family Section */}
                                {hasFamilyContent && (
                                  <Box>
                                    <Typography level="body-xs" textTransform="uppercase" letterSpacing={0.7} sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary' }}>
                                      👨‍👩‍👧 Family Status
                                    </Typography>
                                    <Stack spacing={1.2} sx={{ ml: 0.5 }}>
                                      {['maritalStatus', 'dependents'].map((key) => {
                                        const value = (family as any)[key];
                                        if (!value || (Array.isArray(value) && value.length === 0)) return null;
                                        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
                                        return (
                                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pb: 0.5 }}>
                                            <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                              {label}
                                            </Typography>
                                            <Typography level="body-sm" sx={{ fontWeight: 500, color: 'neutral.700', textAlign: 'right' }}>
                                              {Array.isArray(value) ? value.join(', ') : String(value)}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                )}

                                {/* Devotional Section */}
                                {hasDevotionalContent && (
                                  <Box>
                                    <Typography level="body-xs" textTransform="uppercase" letterSpacing={0.7} sx={{ mb: 1.5, fontWeight: 700, color: 'text.secondary' }}>
                                      🙏 Devotional Commitment
                                    </Typography>
                                    <Stack spacing={1.2} sx={{ ml: 0.5 }}>
                                      {['chantingRounds', 'dailySchedule', 'services'].map((key) => {
                                        const value = (devotional as any)[key];
                                        if (!value || value === 0 || (Array.isArray(value) && value.length === 0)) return null;
                                        const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
                                        return (
                                          <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pb: 0.5 }}>
                                            <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                              {label}
                                            </Typography>
                                            <Typography level="body-sm" sx={{ fontWeight: 500, color: 'neutral.700', textAlign: 'right' }}>
                                              {Array.isArray(value) ? value.join(', ') : String(value)}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Stack>
                                  </Box>
                                )}
                              </>
                            );
                          })()}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Preferences & Communication */}
                {(selectedApplication.preferredMentorId) && (
                  <Card variant="outlined" sx={{ borderColor: 'divider' }}>
                    <CardContent>
                      <Stack spacing={2}>
                        <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                          <FavoriteBorder sx={{ fontSize: 'md', color: 'warning.main' }} />
                          <Typography level="title-sm" fontWeight={700}>Preferences & Communication</Typography>
                        </Stack>
                        <Divider sx={{ my: 0.5 }} />
                        <Stack spacing={1.5}>
                          {selectedApplication.preferredMentorId && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, pb: 0.5 }}>
                              <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>
                                Preferred Mentor ID
                              </Typography>
                              <Typography level="body-sm" sx={{ fontWeight: 500, color: 'neutral.700', textAlign: 'right' }}>
                                {selectedApplication.preferredMentorId}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                )}

                {/* Timeline */}
                <Card variant="outlined" sx={{ borderColor: 'divider' }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                        <Timeline sx={{ fontSize: 'md', color: 'info.main' }} />
                        <Typography level="title-sm" fontWeight={700}>Application Timeline</Typography>
                      </Stack>
                      <Divider sx={{ my: 0.5 }} />
                      {detailLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size="sm" />
                          <Typography level="body-sm">Loading timeline…</Typography>
                        </Stack>
                      ) : currentEvents.length === 0 ? (
                        <Typography level="body-sm" color="neutral">
                          Timeline entries will appear once the application progresses.
                        </Typography>
                      ) : (
                        <Stack spacing={2}>
                          {currentEvents
                            .slice()
                            .sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
                            .map((event, index) => {
                              const eventLabel = event.type
                                ? event.type.replaceAll('_', ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase())
                                : 'Unknown event';

                              const actorDisplay = event.actorName || event.actorRole || 'System';

                              return (
                                <Box
                                  key={event.id}
                                  sx={{
                                    position: 'relative',
                                    pl: 3,
                                    pb: index < currentEvents.length - 1 ? 2 : 0,
                                    '&::before': index < currentEvents.length - 1 ? {
                                      content: '""',
                                      position: 'absolute',
                                      left: '8px',
                                      top: '24px',
                                      bottom: 0,
                                      width: '2px',
                                      bgcolor: 'neutral.outlinedBorder',
                                    } : undefined,
                                  }}
                                >
                                  <Stack
                                    direction="row"
                                    spacing={2}
                                    alignItems="flex-start"
                                  >
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: 0,
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        bgcolor: event.type === 'ACCEPT' || event.type === 'OVERRIDE_ACCEPT'
                                          ? 'success.softBg'
                                          : event.type === 'REJECT' || event.type === 'OVERRIDE_REJECT'
                                            ? 'danger.softBg'
                                            : 'primary.softBg',
                                        border: '2px solid',
                                        borderColor: 'background.surface',
                                      }}
                                    />
                                    <Stack spacing={0.5} sx={{ flex: 1, pt: -0.5 }}>
                                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                        <Chip
                                          size="sm"
                                          variant="soft"
                                          color={
                                            event.type === 'ACCEPT' || event.type === 'OVERRIDE_ACCEPT'
                                              ? 'success'
                                              : event.type === 'REJECT' || event.type === 'OVERRIDE_REJECT'
                                                ? 'danger'
                                                : 'primary'
                                          }
                                        >
                                          {eventLabel}
                                        </Chip>
                                        <Typography level="body-xs" color="neutral">
                                          {dayjs(event.createdAt).format('DD MMM YYYY HH:mm')}
                                        </Typography>
                                      </Stack>
                                      <Typography level="body-sm" fontWeight={500}>
                                        {actorDisplay}
                                      </Typography>
                                      {event.comment && (
                                        <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                          {event.comment}
                                        </Typography>
                                      )}
                                    </Stack>
                                  </Stack>
                                </Box>
                              );
                            })}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>

                {/* Reference Information */}
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: 'divider',
                    bgcolor: 'neutral.softBg',
                  }}
                >
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                        <Info sx={{ fontSize: 'md', color: 'neutral.600' }} />
                        <Typography level="title-sm" fontWeight={700}>Reference Information</Typography>
                      </Stack>
                      <Divider sx={{ my: 0.5 }} />
                      <Stack spacing={1}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                          <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>Application ID</Typography>
                          <Typography level="body-xs" sx={{ fontWeight: 500, color: 'neutral.600', fontFamily: 'monospace' }}>{selectedApplication.id}</Typography>
                        </Box>
                        {applicantId && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>Applicant Member ID</Typography>
                            <Typography level="body-xs" sx={{ fontWeight: 500, color: 'neutral.600', fontFamily: 'monospace' }}>{applicantId}</Typography>
                          </Box>
                        )}
                        {selectedApplication.assignedMentorId && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography level="body-xs" color="neutral" sx={{ fontWeight: 600 }}>Assigned Caretaker ID</Typography>
                            <Typography level="body-xs" sx={{ fontWeight: 500, color: 'neutral.600', fontFamily: 'monospace' }}>{selectedApplication.assignedMentorId}</Typography>
                          </Box>
                        )}
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <Button
                    startDecorator={<AssignmentIndRounded />}
                    onClick={() => {
                      setActionKind('assign');
                      const prefillGuideId = allMentors.find(
                        (guide) =>
                          guide.id === selectedApplication?.assignedMentorId ||
                          guide.memberId === selectedApplication?.assignedMentorId,
                      )?.id ?? null;
                      setAssignState((prev) => ({
                        ...prev,
                        mentorGuideId: prev.mentorGuideId ?? prefillGuideId,
                      }));
                    }}
                    variant="solid"
                    size="sm"
                  >
                    Assign mentor
                  </Button>
                  <Button
                    startDecorator={<SwapHorizRounded />}
                    variant="outlined"
                    color="neutral"
                    onClick={() => setActionKind('override')}
                    size="sm"
                  >
                    Override decision
                  </Button>
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      </Stack>

      <Modal open={actionKind === 'assign'} onClose={() => setActionKind(null)}>
        <ModalDialog aria-labelledby="assign-mentor-modal" sx={{ minWidth: 440 }}>
          <Stack spacing={2}>
            <Typography id="assign-mentor-modal" level="title-lg">
              Assign caretaker
            </Typography>
            <Typography level="body-sm" color="neutral">
              Choose a caretaker to own this application and optionally record an assignment note. Assignment history is preserved
              in the timeline.
            </Typography>
            <FormControl>
              <FormLabel>Caretaker</FormLabel>
              <Select
                placeholder="Select caretaker"
                value={assignState.mentorGuideId ? String(assignState.mentorGuideId) : ''}
                onChange={(_, value) =>
                  setAssignState((prev) => ({
                    ...prev,
                    mentorGuideId: value ? Number(value) : null,
                  }))
                }
              >
                <Option value="">Select caretaker</Option>
                {assignableMentors.map((guide) => (
                  <Option key={guide.id} value={String(guide.id)}>
                    {guide.memberDisplayName}
                  </Option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Assignment note</FormLabel>
              <Textarea
                minRows={3}
                value={assignState.comment}
                onChange={(event) =>
                  setAssignState((prev) => ({
                    ...prev,
                    comment: event.target.value.slice(0, ASSIGN_COMMENT_MAX_LENGTH),
                  }))
                }
                placeholder="Provide context for the assignment (optional)"
                slotProps={{ textarea: { maxLength: ASSIGN_COMMENT_MAX_LENGTH } }}
              />
              <Typography level="body-xs" color="neutral">
                {assignState.comment.length}/{ASSIGN_COMMENT_MAX_LENGTH} characters
              </Typography>
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="plain" color="neutral" onClick={() => setActionKind(null)}>
                Cancel
              </Button>
              <Button
                variant="solid"
                onClick={handleAssignSubmit}
                loading={actionSubmitting}
                startDecorator={<SendRounded />}
              >
                Assign
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal open={actionKind === 'override'} onClose={() => setActionKind(null)}>
        <ModalDialog aria-labelledby="override-modal" sx={{ minWidth: 440 }}>
          <Stack spacing={2}>
            <Typography id="override-modal" level="title-lg">
              Override mentor decision
            </Typography>
            <Typography level="body-sm" color="danger">
              Overrides are audited and notify mentors and applicants immediately. Use when you must finalise a decision on
              behalf of the mentor.
            </Typography>
            <FormControl>
              <FormLabel>Target status</FormLabel>
              <Select
                value={overrideState.targetStatus}
                onChange={(_, value) =>
                  setOverrideState((prev) => ({ ...prev, targetStatus: (value as OverrideTarget) ?? 'ACCEPTED' }))
                }
              >
                <Option value="ACCEPTED">Accept applicant</Option>
                <Option value="REJECTED">Reject applicant</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Justification</FormLabel>
              <Textarea
                minRows={4}
                value={overrideState.comment}
                onChange={(event) =>
                  setOverrideState((prev) => ({
                    ...prev,
                    comment: event.target.value.slice(0, OVERRIDE_COMMENT_MAX_LENGTH),
                  }))
                }
                placeholder={`Explain why the override is required (minimum ${OVERRIDE_COMMENT_MIN_LENGTH} characters, maximum ${OVERRIDE_COMMENT_MAX_LENGTH}).`}
                slotProps={{ textarea: { maxLength: OVERRIDE_COMMENT_MAX_LENGTH } }}
              />
              <Typography level="body-xs" color="neutral">
                {overrideState.comment.length}/{OVERRIDE_COMMENT_MAX_LENGTH} characters
              </Typography>
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="plain" color="neutral" onClick={() => setActionKind(null)}>
                Cancel
              </Button>
              <Button
                variant="solid"
                color={overrideState.targetStatus === 'ACCEPTED' ? 'success' : 'danger'}
                startDecorator={overrideState.targetStatus === 'ACCEPTED' ? <DoneAllRounded /> : <ErrorOutlineRounded />}
                loading={actionSubmitting}
                onClick={handleOverrideSubmit}
              >
                Confirm override
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
